import React, { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { useApiData } from '../hooks/useApiData';
import { EmptyState, ErrorState, LoadingState, MetricBar } from '../components/primitives';
import RangeControl, { rangeLabel } from '../components/RangeControl';
import { SessionRow, TimeRange } from '../types/superadmin';
import { clientPath, rangeFromQuery, rangeToQuery, sessionPath } from '../lib/rangeParams';
import {
  analysisLabel,
  analysisTone,
  formatDuration,
  formatSessionTime,
  sourceLabel,
  statusTone
} from '../lib/session';
import { fmtInt } from '../lib/format';

/**
 * One rep's calls inside the selected period.
 *
 * Straight from the client's live database, not the nightly rollup — a super
 * admin opening a rep's calls wants the calls, including any recorded since the
 * last rollup ran.
 *
 * ⚠️ The count here can exceed the "Sessions" number on the client drill-down
 * by one or two: the rollup also removes duplicate recordings of a single
 * meeting, which needs a per-tenant pre-pass too heavy to run per request. See
 * `backend-tenant/src/services/tenantSessions.js`.
 */

export default function RepSessions() {
  const { tenantId, userId } = useParams<{ tenantId: string; userId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const range = rangeFromQuery(searchParams);
  const [page, setPage] = useState(1);

  const setRange = (next: TimeRange) => {
    setPage(1);
    setSearchParams(new URLSearchParams(rangeToQuery(next)), { replace: true });
  };

  const { data, loading, error } = useApiData(
    () => api.getRepSessions(tenantId as string, userId as string, range, page),
    [tenantId, userId, JSON.stringify(range), page]
  );

  if (loading) return <LoadingState label="Loading sessions…" />;
  if (error || !data?.data) return <ErrorState message={error || 'Sessions not found.'} />;

  const { sessions, displayName } = data.data;
  const pagination = data.meta?.pagination;
  const totalPages = pagination?.pages ?? 1;
  // Every row belongs to one rep, so the rep's name is whatever the sessions
  // agree on — the roster is not fetched again just for a heading.
  const repEmail = sessions[0]?.userEmail ?? null;

  return (
    <>
      <div className="filter-bar">
        <Link className="dd" to={clientPath(tenantId as string, range)}>
          ← {displayName}
        </Link>
        <span className="sep" />
        <span className="label">Sessions</span>
        <span className="dd-pill">{repEmail || userId}</span>
        <span className="grow" />
        <RangeControl value={range} onChange={setRange} />
      </div>

      <div className="head">
        <div>
          <div className="eyebrow">Client · rep · sessions</div>
          <h1 className="title">
            <span className="serif">Session review</span>
          </h1>
          <p className="subtitle">
            {fmtInt(pagination?.total ?? sessions.length)} session
            {(pagination?.total ?? sessions.length) === 1 ? '' : 's'} for {repEmail || 'this rep'} at{' '}
            {displayName}, <b>{rangeLabel(range)}</b>. Open one to read its transcript and Discovery
            Call Rubric result.
          </p>
        </div>
      </div>

      <section className="section">
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>Session</th>
                <th style={{ width: 170 }}>Started</th>
                <th style={{ width: 110 }}>Duration</th>
                <th style={{ width: 160 }}>Captured by</th>
                <th style={{ width: 100 }}>Lines</th>
                <th style={{ width: 190 }}>Call quality</th>
                <th style={{ width: 130 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((session: SessionRow) => (
                <tr
                  key={session.sessionId}
                  className="clickable"
                  onClick={() => navigate(sessionPath(tenantId as string, session.sessionId, range))}
                >
                  <td>
                    <div className="client">
                      <span>{session.sessionName}</span>
                    </div>
                    <div className="sub-row mono">{session.sessionId.slice(0, 28)}…</div>
                  </td>
                  <td>{formatSessionTime(session.callStartTime)}</td>
                  <td>
                    <span className="metric-num">{formatDuration(session.callDuration)}</span>
                  </td>
                  <td>
                    <span className="chip">{sourceLabel(session.source, session.platform)}</span>
                  </td>
                  <td>
                    <span className="metric-num">{fmtInt(session.transcriptCount)}</span>
                  </td>
                  <td>
                    {session.score.percentage != null ? (
                      <MetricBar value={session.score.percentage} />
                    ) : (
                      <span className={'chip ' + analysisTone(session.score.status)}>
                        {analysisLabel(session.score.status)}
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={'chip ' + statusTone(session.status)}>{session.status}</span>
                  </td>
                </tr>
              ))}
              {sessions.length === 0 && (
                <tr>
                  <td colSpan={7}>
                    <EmptyState>This rep had no calls in {rangeLabel(range)}.</EmptyState>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="pager">
            <button type="button" className="btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              ← Previous
            </button>
            <span className="caption">
              Page {page} of {totalPages} · {pagination?.limit ?? sessions.length} per page
            </span>
            <button
              type="button"
              className="btn"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next →
            </button>
          </div>
        )}
      </section>
    </>
  );
}
